import { useState, useCallback, useEffect } from "react";
import {
  useQuery,
  ApolloError,
  DocumentNode,
  QueryHookOptions,
  OperationVariables
} from "@apollo/client";

interface PageInfo {
  endCursor: string | null;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  startCursor: string | null;
}

interface PaginatedResponse<TNode> {
  pageInfo: PageInfo;
  totalCount: number;
  data: TNode[];
}

interface UsePaginatedQueryParams<TVariables, TNode> {
  query: DocumentNode;
  variables?: TVariables;
  cursorField: string;
  pageSize?: number;
  options?: Omit<
    QueryHookOptions<
      GetPaginatedData<TNode>,
      TVariables extends OperationVariables ? TVariables : never
    >,
    "variables"
  >;
  loadMore?: boolean;
}

interface GetPaginatedData<TNode> {
  [key: string]: PaginatedResponse<TNode>;
}

interface UsePaginatedQueryReturn<TNode> {
  data: TNode[];
  pageInfo: PageInfo;
  totalCount: number;
  loading: boolean;
  error: ApolloError | undefined;
  loadNextPage: () => void;
  loadPreviousPage: () => void;
  goToPage: (pageNumber: number) => void;
  setPageSize: (newPageSize: number) => void;
  resetPagination: () => void;
  currentPage: number;
}

export default function usePaginatedQuery<TVariables extends OperationVariables, TNode>({
  query,
  variables = {} as TVariables,
  cursorField,
  pageSize: initialPageSize = 10,
  options,
  loadMore
}: UsePaginatedQueryParams<TVariables, TNode>): UsePaginatedQueryReturn<TNode> {
  const [pagination, setPagination] = useState<{
    after: string | null;
    before: string | null;
    first: number | null;
    last: number | null;
    currentPage: number;
    pageSize: number;
  }>({
    after: null,
    before: null,
    first: initialPageSize,
    last: null,
    currentPage: 1,
    pageSize: initialPageSize
  });

  const [pagesData, setPagesData] = useState<Map<number, TNode[]>>(new Map());
  const [totalCount, setTotalCount] = useState(0);
  const [pageInfo, setPageInfo] = useState<PageInfo>({
    endCursor: null,
    hasNextPage: false,
    hasPreviousPage: false,
    startCursor: null
  });

  const { data, loading, error } = useQuery<GetPaginatedData<TNode>, TVariables>(query, {
    variables: {
      ...variables,
      first: pagination.first,
      after: pagination.after,
      before: pagination.before,
      last: pagination.last
    },
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
    ...options
  });

  const paginatedData = data?.[cursorField] as PaginatedResponse<TNode> | undefined;

  useEffect(() => {
    if (!paginatedData?.data) return;

    if (!loadMore) {
      setPagesData(new Map([[pagination.currentPage, paginatedData.data]]));
    } else {
      setPagesData((prev) => {
        const newPages = new Map(prev);
        newPages.set(pagination.currentPage, paginatedData.data);
        return newPages;
      });
    }

    setTotalCount(paginatedData.totalCount);
    setPageInfo(paginatedData.pageInfo);
  }, [JSON.stringify(paginatedData), pagination.currentPage, loadMore]);

  useEffect(() => {
    return () => {
      setPagesData(new Map());
      setTotalCount(0);
      setPagination({
        after: null,
        before: null,
        first: initialPageSize,
        last: null,
        currentPage: 1,
        pageSize: initialPageSize
      });
    };
  }, [initialPageSize]);

  const loadNextPage = useCallback(() => {
    if (pageInfo.hasNextPage) {
      setPagination((prev) => ({
        ...prev,
        after: pageInfo.endCursor,
        before: null,
        first: prev.pageSize,
        last: null,
        currentPage: prev.currentPage + 1
      }));
    }
  }, [pageInfo]);

  const loadPreviousPage = useCallback(() => {
    if (pageInfo.hasPreviousPage) {
      setPagination((prev) => ({
        ...prev,
        after: null,
        before: pageInfo.startCursor,
        first: null,
        last: prev.pageSize,
        currentPage: Math.max(prev.currentPage - 1, 1)
      }));
    }
  }, [pageInfo]);

  const goToPage = useCallback(
    (pageNumber: number) => {
      const offset = (pageNumber - 1) * pagination.pageSize;
      const offsetToCursor = (offset: number) => btoa(`arrayconnection:${offset}`);
      const cursor = offsetToCursor(offset);
      setPagination({
        after: cursor,
        before: null,
        first: pagination.pageSize,
        last: null,
        currentPage: pageNumber,
        pageSize: pagination.pageSize
      });
    },
    [pagination.pageSize]
  );

  const setPageSizeFn = useCallback((newPageSize: number) => {
    setPagesData(new Map());
    setPagination({
      after: null,
      before: null,
      first: newPageSize,
      last: null,
      currentPage: 1,
      pageSize: newPageSize
    });
    setTotalCount(0);
  }, []);

  const resetPagination = useCallback(() => {
    setPagesData(new Map());
    setPagination({
      after: null,
      before: null,
      first: initialPageSize,
      last: null,
      currentPage: 1,
      pageSize: initialPageSize
    });
    setTotalCount(0);
  }, [initialPageSize]);

  return {
    data: Array.from(pagesData.values()).flat(),
    pageInfo,
    totalCount,
    loading,
    error,
    loadNextPage,
    loadPreviousPage,
    goToPage,
    setPageSize: setPageSizeFn,
    resetPagination,
    currentPage: pagination.currentPage
  };
}
